import { useState } from 'react';
import { Button, Dialog, Select } from '@schibsted-svp/react-ui';
import { useFormikContext } from 'formik';

import { TagsSelect } from '../../Form/TagsSelect/TagsSelect';
import { StoriesSelect } from '../../Form/StoriesSelect/StoriesSelect';
import { FormField } from '../../Form/shared/FormField/FormField';
import { CategoriesSelect } from '../../Form/CategoriesSelect/CategoriesSelect';
import { FILTER_TAG, FILTER_STORY, FILTER_CATEGORY } from '../types';

import type { FC } from 'react';
import type { Option } from '../../Form/Select/Select';
import type { Filter } from '../types';

// the tags, stories and categories async select boxes are now coupled with formik,
// thus -> the input name and clearing the formik state when a filter is set,
// it would be ideal if they could be used outside of a formik form
const FORMIK_FIELD_NAME = 'filter';

type Props = {
  isOpen?: boolean;
  onClose: () => void;
  onAdd: (filter: Filter) => void;
  filters: Option[];
};

const AddFilterDialog: FC<Props> = ({
  isOpen = false,
  onClose,
  onAdd,
  filters,
}) => {
  const [type, setType] = useState<Option>(filters[0]);
  const [filter, setFilter] = useState<Option | null>(null);
  const { setFieldValue } = useFormikContext();
  const typeValue = type.value;

  async function onChange(option?: Option | Option[]) {
    setFilter((option as Option) ?? null);
  }

  function onAddClick() {
    const values: Filter = {
      type: typeValue as Filter['type'],
    };

    if (!filter) {
      return;
    }

    if (typeValue === FILTER_TAG) {
      values.tag = {
        id: filter.value,
        title: filter.label,
      };
    }

    if (typeValue === FILTER_STORY) {
      values.story = {
        id: filter.value,
        title: filter.label,
      };
    }

    if (typeValue === FILTER_CATEGORY) {
      values.category = {
        id: filter.value,
        title: filter.label,
      };
    }

    onAdd(values);
    setFieldValue(FORMIK_FIELD_NAME, undefined);
    onClose();
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} heading="Add a filter">
      <Dialog.Section>
        <FormField label="Type">
          <Select
            options={filters}
            onChange={(option: Option) => {
              setType(option);
            }}
            value={type}
          />
        </FormField>
        {typeValue === FILTER_TAG && (
          <TagsSelect
            label="Tag"
            onChange={onChange}
            name={FORMIK_FIELD_NAME}
          />
        )}
        {typeValue === FILTER_STORY && (
          <StoriesSelect
            label="Story"
            onChange={onChange}
            name={FORMIK_FIELD_NAME}
          />
        )}
        {typeValue === FILTER_CATEGORY && (
          <CategoriesSelect
            label="Category"
            name={FORMIK_FIELD_NAME}
            onChange={onChange}
          />
        )}
      </Dialog.Section>
      <Dialog.Section variant="darker" mode="flexRight">
        <Button type="button" onClick={onClose} variant="standard">
          Cancel
        </Button>
        <Button type="button" onClick={onAddClick} disabled={filter === null}>
          Add
        </Button>
      </Dialog.Section>
    </Dialog>
  );
};

export { AddFilterDialog };
